import type { MaybeRefOrGetter, Ref } from 'vue'
import { toValue, watch } from 'vue'
import { EventListPageType } from '~/composables/types/strapiEventListPage'
import type { SearchFilterOptions } from '~/composables/useSearchFilters'
import {
  useFilterNavigation,
  type FilterNavigationParams,
  type FilterNavigationResult,
} from '~/composables/transactionalPage/useFilterNavigation'
import {
  transactionalFilterStructuralFieldsChanged,
  computeTransactionalFilterForceSearch,
  resolveStrapiCategorySlugForNavigation,
} from '~/utils/transactionalFilterNavigationDecision'

export interface TransactionalFilterNavigationRunInput {
  isClient: boolean
  pageType: EventListPageType
  newLocation: unknown
  oldLocation: unknown
  newCategories: unknown
  oldCategories: unknown
  categoryNameToSlug: Record<string, string>
  routeCategorySlug: string | undefined
  filters: SearchFilterOptions
  teamEventSlugPrefix: string
  resolve: (params: FilterNavigationParams) => Promise<FilterNavigationResult>
}

/**
 * Implements the transactional filter navigation decision from
 * `FilteredProductShowcase`: when location or categories change structurally,
 * optionally clears tag filters, resolves the next path (Strapi check or
 * search fallback), and returns it. Returns `null` when no navigation should
 * run (SSR, non-transactional page types, or no structural change).
 *
 * Mutates `input.filters.tags` (sets to `[]`) before calling `resolve`, matching
 * previous in-component behavior.
 */
export const runTransactionalFilterNavigationIfNeeded = async (
  input: TransactionalFilterNavigationRunInput
): Promise<FilterNavigationResult | null> => {
  if (!input.isClient) return null

  if (
    input.pageType === EventListPageType.SEARCH ||
    input.pageType === EventListPageType.GENERAL ||
    input.pageType === EventListPageType.KITS
  ) {
    return null
  }

  const categoriesArray = Array.isArray(input.newCategories)
    ? input.newCategories
    : []

  if (
    !transactionalFilterStructuralFieldsChanged(
      input.newLocation,
      input.oldLocation,
      input.newCategories,
      input.oldCategories
    )
  ) {
    return null
  }

  const forceSearch = computeTransactionalFilterForceSearch(
    input.pageType,
    input.newLocation as string | undefined,
    categoriesArray
  )

  const targetCategory = resolveStrapiCategorySlugForNavigation({
    categories: categoriesArray,
    nameToSlug: input.categoryNameToSlug,
    routeCategorySlug: input.routeCategorySlug,
  })

  const strapiListPageSlug =
    input.pageType === EventListPageType.TEAMEVENT && input.newLocation
      ? `${input.teamEventSlugPrefix}-${String(input.newLocation)}`
      : undefined

  input.filters.tags = []

  return input.resolve({
    categorySlug: targetCategory,
    filters: input.filters,
    forceSearch,
    strapiListPageSlug,
  })
}

export interface UseTransactionalFilterNavigationWatchOptions {
  pageType: MaybeRefOrGetter<EventListPageType>
  filters: Ref<SearchFilterOptions>
  categoryNameToSlug: MaybeRefOrGetter<Record<string, string>>
  categorySlug: MaybeRefOrGetter<string | undefined>
}

/**
 * When location or categories change on a transactional page, evaluate
 * whether to navigate to another transactional URL or fall back to search.
 */
export const useTransactionalFilterNavigationWatch = (
  options: UseTransactionalFilterNavigationWatchOptions
) => {
  const router = useRouter()
  const { resolve } = useFilterNavigation()
  const { t } = useI18n()

  watch(
    () =>
      [
        options.filters.value.location,
        options.filters.value.locationType,
        options.filters.value.categories,
      ] as const,
    async (
      [newLocation, _newLocationType, newCategories],
      [oldLocation, _oldLocationType, oldCategories]
    ) => {
      const result = await runTransactionalFilterNavigationIfNeeded({
        isClient: import.meta.client,
        pageType: toValue(options.pageType),
        newLocation,
        oldLocation,
        newCategories,
        oldCategories,
        categoryNameToSlug: toValue(options.categoryNameToSlug),
        routeCategorySlug: toValue(options.categorySlug),
        filters: options.filters.value,
        teamEventSlugPrefix: t('eventListPage.teamEventSlugPrefix'),
        resolve,
      })

      if (result) await router.push(result.path)
    },
    { deep: true, flush: 'post' }
  )
}
